<template>
  <div class="issue-add-edit-popup-core-content">
    <div class="popup-container">
      <div class="popup-title">{{ $t(title) }}</div>
      <div class="close" @click="onCloseClickButton">X {{ $t('newProject.esc') }}</div>

      <div class="input-title">{{ $t('issueDataPopup.securityRisk') }}</div>
      <v-autocomplete
        :items="issuesInnerData.securityRisks"
        class="select-security-risk"
        v-model="currentIssue.securityRisk"
        return-object
        item-text="name"
        dense
        outlined
        :placeholder="$t('issueDataPopup.selectSecurityRisk')"
      >
      </v-autocomplete>

      <span class="description-block">
        <div class="input-title">{{ $t('issueDataPopup.description') }}</div>
        <span class="text-area-container">
          <textarea
            v-model="currentIssue.description"
            maxlength="255"
            :placeholder="$t('issueDataPopup.enterDescription')"
            class="input-description input-textarea"
          ></textarea>
        </span>
      </span>
      <div class="three-inputs-row">
        <div class="three-inputs-column">
          <div class="input-title">{{ $t('issueDataPopup.severity') }}</div>
          <v-autocomplete
            :items="issuesInnerData.severities"
            class="select-severities"
            v-model="currentIssue.severity"
            item-text="name"
            dense
            outlined
            :placeholder="$t('issueDataPopup.select')"
          >
          </v-autocomplete>
        </div>
        <div class="three-inputs-column">
          <div class="input-title">{{ $t('issueDataPopup.probability') }}</div>
          <v-autocomplete
            :items="issuesInnerData.probabilities"
            class="select-probability"
            v-model="currentIssue.probability"
            item-text="name"
            dense
            outlined
            :placeholder="$t('issueDataPopup.select')"
          >
          </v-autocomplete>
        </div>
        <div class="three-inputs-column">
          <div class="input-title">{{ $t('issueDataPopup.riskScore') }}</div>
          <span class="input-number-container">
            <input
              type="number"
              min="1"
              max="10"
              step="0.1"
              :placeholder="$t('issueDataPopup.setNumber')"
              v-model="currentIssue.riskScore"
              @input="onRiskScoreChanged"
            />
          </span>
        </div>
        <span class="comment-block">
          <div class="input-title">{{ $t('issueDataPopup.mitigation') }}</div>
          <span class="text-area-container">
            <textarea
              v-model="currentIssue.comment"
              maxlength="2000"
              :placeholder="$t('issueDataPopup.enterMitigation')"
              class="input-comment input-textarea"
            ></textarea>
          </span>
        </span>
      </div>
      <div class="save-button-container">
        <div :class="{ 'can-save': canSave }" @click="canSave && save()" :disabled="!canSave" class="save-button">
          {{ $t('issueDataPopup.save') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';

import { LOAD_ISSUES_INNER_DATA } from '@/store/issue/actionTypes';

export default {
  name: 'IssueAddEditPopupCoreContent',
  props: {
    currentIssue: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters(['issuesInnerData']),
    canSave() {
      if (!Object.keys(this.currentIssue).length) {
        return false;
      }
      return (
        this.currentIssue.securityRisk?.trim().length &&
        this.currentIssue.description?.trim().length &&
        this.currentIssue.probability?.trim().length &&
        this.currentIssue.severity?.trim().length &&
        (this.currentIssue.riskScore || this.currentIssue.riskScore === 0)
      );
    },
  },
  async beforeMount() {
    await this.$store.dispatch(LOAD_ISSUES_INNER_DATA);
  },
  methods: {
    onCloseClickButton() {
      this.$emit('closed', true);
    },
    onRiskScoreChanged() {
      if (this.currentIssue.riskScore < 0) {
        this.currentIssue.riskScore = 0;
      } else {
        this.currentIssue.riskScore = this.currentIssue.riskScore > 10 ? 10 : this.currentIssue.riskScore;
      }
    },
    save() {
      this.$emit('saved', _.cloneDeep(this.currentIssue));
      this.onCloseClickButton();
    },
  },
};
</script>

<style lang="scss">
.issue-add-edit-popup-core-content.v-menu__content {
  .v-list-item__title {
    text-transform: capitalize;
  }
}

.three-inputs-column {
  .v-select__slot {
    input {
      text-transform: capitalize;
    }
  }
}
</style>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.popup-container {
  width: rem(560);
  height: rem(675);
  overflow-y: auto;
  border-radius: $radius-md;
  background-color: var(--bg-primary);
  box-shadow: 0 0 rem(11) 0 rgba(12, 18, 62, 0.13);
  padding-left: $space-15;
  padding-top: $space-11;
  top: rem(60);
  position: relative;
  margin: auto;

  .popup-title {
    color: var(--text-primary);
    font-size: $font-size-3xl;
    font-weight: bold;
    letter-spacing: 0;
    line-height: rem(28);
    margin-bottom: $space-6;
  }

  .close {
    cursor: pointer;
    display: inline-block;
    position: absolute;
    right: $space-2;
    top: $space-2;
    font-size: $font-size-sm;
    font-weight: 500;
    letter-spacing: rem(1.07);
    line-height: rem(16);
    color: var(--link-primary);
  }

  ::placeholder {
    color: var(--text-muted);
  }

  .input-title {
    color: var(--text-secondary);
    font-size: $font-size-xs;
    letter-spacing: rem(0.8);
    line-height: rem(10);
    text-transform: uppercase;
  }

  .select-security-risk {
    top: rem(-12);
    box-sizing: border-box;
    border-radius: $radius-sm;
    background-color: var(--bg-primary);
    height: rem(34);
    width: rem(440);
  }

  .description-block {
    margin-top: $space-2;
    display: block;

    .text-area-container {
      textarea {
        width: rem(440);
        height: rem(86);
      }
    }
  }

  .comment-block {
    margin-top: $space-2;
    display: block;

    .text-area-container {
      textarea {
        width: rem(440);
        height: rem(165);
      }
    }
  }

  .text-area-container {
    textarea {
      resize: none;
      padding: $space-2 $space-2 $space-2 $space-3;

      &:focus {
        outline: none;
        border: $border-width-thin solid var(--input-border-focus);
        box-shadow: 0 0 rem(10) #719ece;
      }
    }
    .input-textarea {
      margin-top: rem(5);
      border: $border-width-thin solid var(--border-primary);
      border-radius: $radius-sm;
    }
  }

  .three-inputs-row {
    margin-top: $space-4;

    .three-inputs-column {
      display: inline-block;
      width: rem(136);
      margin-right: $space-4;

      .v-input {
        .v-input__control {
          .v-input__slot {
            .v-select__slot {
              input {
                text-transform: capitalize;
              }
            }
          }
        }
      }

      .select-severities {
        margin-top: rem(5);
      }

      .select-probability {
        margin-top: rem(5);
      }

      .input-number-container {
        input {
          height: $btn-height-md;
          width: 100%;
          padding: $space-2 $space-2 $space-2 $space-3;
          margin-top: rem(5);
          border: $border-width-thin solid var(--border-primary);
          border-radius: $radius-sm;

          &:focus {
            outline: none;
            border: $border-width-thin solid var(--input-border-focus);
          }
        }
      }
    }
  }

  .save-button-container {
    float: right;
    margin-right: $space-15;
    margin-top: $space-14;

    .save-button {
      height: $btn-height-md;
      width: rem(66);
      border-radius: $radius-sm;
      background-color: lightslategray;
      box-shadow: 0 rem(1) rem(1) 0 rgba(0, 0, 0, 0.14), 0 rem(2) rem(1) rem(-1) rgba(0, 0, 0, 0.12), 0 rem(1) rem(3) 0 rgba(0, 0, 0, 0.2);
      color: var(--text-inverse);
      font-size: $font-size-sm;
      font-weight: 500;
      letter-spacing: rem(1.07);
      line-height: rem(16);
      text-align: center;
      text-transform: uppercase;
      padding-top: $space-2;
      cursor: default;

      &.can-save {
        background-color: #4d9feb;
        cursor: pointer;
      }
    }
  }
}
</style>
